/**
 * Submitter ID from app props will be either a number or "new", if present.
 * See: app/models/registration/submitter_id_param.rb
 */
import { LicenseTypeName, ParticipantType, TPAUse, PBMUse, CarveOutPayerUse, InsurerTypeName, InsurerType } from "./clients/config"

// Although config is now source of authority for these, they are re-
// exported here to avoid breaking existing code. In a sense, both
// modules share the authority.
export { NOT_APPLICABLE, OTHER } from "./clients/config"

export type SubmitterIdParam = number | "new"

export const YES = "yes"
export const NO = "no"
export type YES = typeof YES
export type NO = typeof NO
export type YES_OR_NO = YES | NO

/**
 * The form object is a key-value store with no guaranteed shape or depth. It can be
 * mutated at any time and in any way by the form event handlers. The properties listed
 * in this interface are only some of the attributes it usually has as it grows from
 * being an empty object to a huge dictionary.
 */
export interface IRegistrationForm extends Record<string, unknown>, FormCoverageEstimates {
  /** The submitter association the user selected when they started the form */
  submitter_id?: SubmitterId | "new" | null

  /**
   * Repository for all contacts.
   */
  contacts?: IContactStore

  /**
   * Contains all plans referenced by claims section, if any. (BH-5904)
   *
   * key = plan code
   * value = plan
   */
  plans?: Record<string, IPlanParentOrganization>

  /**
   * Reference to the current user. ("Organization" step)
   */
  filler?: IContactRef

  /**
   * The company/submitter/organization section. ("Organization" step)
   */
  insurer?: IInsurer

  // Additional contact references for the "Contacts" step:
  program_compliance?: IContactRef
  legal_advisory?: IContactRef
  sftp?: IContactRef
  lockbox_enrollment_submissions?: IContactRef
  crisp_enrollment_submissions?: IContactRef

  enrollment_submissions?: IContactRef
  medical_claims_submission?: IContactRef
  pharmacy_claims_submission?: IContactRef
  provider_file_submissions?: IContactRef
  member_identifier_submissions?: IContactRef

  technical_support_1?: IContactRef
  technical_support_2?: IContactRef

  business?: IContactRef
  regulatory?: IContactRef
  technical?: IContactRef
  annual_payments?: IContactRef
  pharmacy_rebates?: IContactRef
  capitation?: IContactRef

  // CO contact types (see DO-562):
  alternative_payment_model?: IContactRef
  control_total?: IContactRef
  pbm_contract?: IContactRef
  prescription_affordability?: IContactRef
  value_based_contract?: IContactRef
  apm_contract?: IContactRef

  // DO-270 MN contact type:
  non_claims_payments?: IContactRef

  // DO-1052 IN contact types:
  commissions_and_brokerage?: IContactRef
  provider_price_transparency?: IContactRef

  /**
   * Actually the comments for some reason:
   */
  additional_contacts?: {
    text?: string
  }

  eligibility?: IEligibility
  submission_schedule?: ISubmissionSchedule
  certification?: ICertification

  "max-step"?: number

  tpa?: ITPA
  pbm?: IPBM
  carve_out_payer?: ICarveOutPayer

  /**
   * Additional info about self-insured clients if the submitter is a TPA.
   * Added for MN in DO-270.
   */
  tpa_self_insured?: ITpaSelfInsured
}

export interface FormCoverageEstimates {
  medical?: ICoverageEstimatesTable
  pharmacy?: ICoverageEstimatesTable
  dental?: ICoverageEstimatesTable
}

export type UUID = string

export interface IContactStore {
  [uuid: string]: IContact | undefined
}

/**
 * Data type owned by ContactFillingForm.
 */
export interface IContactRef {
  /**
   * Pointer to the actual contact referenced. The same as new_uuid if the user entered a new Contact
   * in the "slot".
   */
  contact?: string

  /**
   * Pointer to a (potentially empty) new contact associated with the ContactFillingForm. Never changed once generated.
   */
  new_uuid?: string

  /**
   * Points to a plan record by plan code, if plan selection is enabled for this client. (BH-5904)
   */
  plan_codes?: ISelectedPlanCode[]
}

export interface ISelectedPlanCode {
  code: string
  other_name?: string
}

export interface IContact extends IAddress {
  uuid?: UUID // cannot assume uuid is set, although it often is
  id?: number // only persisted contacts will have an id
  first_name?: string
  last_name?: string
  company_name?: string
  organization?: string
  email?: string
  email_confirmation?: string
  phone?: string
  phone_extension?: string
  title?: string
  notification_opt_in?: YES_OR_NO
  "work-with"?: string
}

/**
 * Use this type when a contact with guaranteed uuid is required.
 */
export interface IContactWithUUID extends IContact {
  uuid: string
}

export interface IInsurer extends IAddress {
  company_name?: string
  /**
   * OHCA-only
   */
  parent_company_name?: string
  custom_parent_company_name?: string
  /**
   * HCAI-only (actual label: "Submitter Parent Name")
   */
  payer_name?: string
  manually_edited_company?: boolean
  submitter_code?: string
  type?: InsurerTypeName
  /**
   * OHCA-only replacement for "type"
   */
  participant_type?: ParticipantType
  feins?: string[]
  naics?: string[]
  /**
   * OHCA-only multi-select that appears below NAICs
   */
  license_types?: ISelectedLicenceType[]
  /**
   * OHCA-only multi-select that appears below license types.
   * Market categories are CA-specific.
   */
  market_categories?: (string|undefined)[]
  /**
   * Added in DO-263 after market categories initial release,
   * so we unfortunately represent this data as a separate array.
   */
  market_category_covered_lives?: (string|undefined)[]
}

export interface ISelectedLicenceType {
  /**
   * A loose key pointing to a license type.
   */
  name?: LicenseTypeName
  custom_name?: string
  /**
   * license_entity_name is a new field for OHCA submitter registration:
   */
  licensed_entity_name?: string
  license_number?: string
}

export const HasQualificationSections: Set<InsurerTypeName> = new Set([
  InsurerType.BehavioralHealthCarveOutPayer,
  InsurerType.GovernmentProvider,
  InsurerType.InsuranceCompany,
  InsurerType.InsuranceCompanyLegacy,
  InsurerType.PharmacyBenefitsManager,
  InsurerType.ThirdPartyAdministrator,
])

export const HasCarveOutSections: Set<InsurerTypeName> = new Set([
  InsurerType.GovernmentProvider,
  InsurerType.InsuranceCompany,
  InsurerType.InsuranceCompanyLegacy,
  InsurerType.Employer,
])

export const HasCoverageEstimatesSections: Set<InsurerTypeName> = new Set([
  InsurerType.BehavioralHealthCarveOutPayer,
  InsurerType.Employer,
  InsurerType.GovernmentProvider,
  InsurerType.InsuranceCompany,
  InsurerType.InsuranceCompanyLegacy,
  InsurerType.PharmacyBenefitsManager,
  InsurerType.ThirdPartyAdministrator,
])

export interface IAddress {
  street_1?: string
  street_2?: string
  city?: string
  state?: string
  zip?: string
  country_code?: string
  zip_found?: boolean
}

export interface APIAddress {
  address1?: string
  address2?: string
  city?: string
  state?: string
  zip?: string
  country?: string
}

export type ClientId = number

export interface IClient {
  slug: string
  short_name: string
  long_name: string
  registration_form_url: string
  requires_qualification: boolean
  component_name: string
  intro_text: string
  about_the_process_text: string
  has_registration_attachment: boolean
  has_tpa: boolean
  has_pbm: boolean
  has_carve_out_payer: boolean
  has_schedule: boolean
  registration_attachment_label?: string | null
  qualification_text: string
  qualification_confirmation_text: string
  qualification_rejection_text: string
  registration_attachment_text?: string | null
  thank_you_text: string
  certification_agreement_text?: string | null
}

export type SubmitterId = number

export interface ISubmitter {
  id: SubmitterId
  code: string | null
  name: string
}

export interface IEligibility {
  reporting?: typeof YES | typeof NO
  qualification_date?: string
  reason_for_not_reporting?: string
}

export interface ISubmissionSchedule {
  frequency?: string
}

/**
 * Maryland certification form data.
 */
export interface ICertification {
  certifier_name?: string
  certifier_signature?: string
  certifier_job_title?: string
  certifier_current_phone_number?: string
  certifier_current_email_address?: string
  datetime?: string
  certified?: string
}

export interface IPlanParentOrganization {
  id: number
  refFileType: IRefFileType
  planId: number
  planCode: string
  planName: string
  parentOrganizationName: string
  organizationAddress: APIAddress
  planAddress: APIAddress
}

export interface IActiveSubmitter {
  id: number
  submitterCode: string
  // companyShortName: string | null // it's there, but prevent using it by accident
  companyLongName: string
}

export interface IActiveSubmittersResponse {
  success: true
  results: IActiveSubmitter[]
}

export interface IRefFileType {
  id: number
  fileTypeName: string
  fileTypeCode: string
}

export interface ITPA {
  /**
   * This field is now used to represent one of three radio button selections.
   */
  "work-with"?: TPAUse
  company_name?: string
  custom_company_name?: string
  organization?: string
  naics?: string[]
  feins?: string[]
}

export interface IPBM {
  "work-with"?: PBMUse
  company_name?: string
  custom_company_name?: string
  organization?: string
  naics?: string[]
  feins?: string[]
  pbm_registration_number?: string
}

export interface ICarveOutPayer {
  "work-with"?: CarveOutPayerUse
  company_name?: string
  custom_company_name?: string
  organization?: string
  naics?: string[]
  feins?: string[]
}

export const SHOW_CLAIM_INFO_FOR = [
  TPAUse.WE_ARE_THE_TPA,
  TPAUse.WE_HAVE_A_TPA,
  PBMUse.WE_ARE_THE_PBM,
  PBMUse.WE_HAVE_A_PBM,
  CarveOutPayerUse.WE_HAVE_A_CARVE_OUT_PAYER,
  CarveOutPayerUse.WE_ARE_THE_CARVE_OUT_PAYER,
]

export interface IComponentStore<T> {
  [key: string]: ((props: T) => React.ReactNode) | undefined
}

export interface Class<T> {
  new (...args: any[]): T
}

export interface ICoverageEstimatesTable {
  claim_value?: IClaimValue_PointlessNesting
  any_covered_lives?: YES_OR_NO
}

export interface IClaimValue_PointlessNesting {
  covered_lives?: ICoverageEstimatesColumn
  claim_volume?: ICoverageEstimatesColumn
  claim_value?: ICoverageEstimatesColumn
}

export interface ICoverageEstimatesColumn {
  totals?: string
  private?: IPrivate
  state?: IState
  medicare?: IMedicare
  medicaid?: IMedicaid
  other?: IOther
}

export interface IPrivate {
  small_employer?: string
  large_employer?: string
  exchange_plans?: string
  individual_direct_purchase_plans?: string
  self_insured_erisa?: string
  self_insured_non_erisa?: string
}

export interface IState {
  state_benefit_health_plans?: string
}

export interface IMedicare {
  ffs?: string
  part_c?: string
  /**
   * Part-D is pharmacy-only.
   */
  part_d?: string
  supplemental?: string
  /**
   * Medicare Medicaid Dual Special Needs Plan (D-SNP)
   * (Not on dental form)
   */
  d_snp?: string
}

export interface IMedicaid {
  ffs?: string
  managed_care?: string
}

export interface IOther {
  other?: string
}

export type KeyOfCoverageEstimateType = keyof IPrivate | keyof IState | keyof IMedicare | keyof IMedicaid | keyof IOther

export interface IAttachment {
  filename: string
  url: string
  safe: boolean
  metadata?: {
    identified?: boolean
    analyzed_mime_type?: string
    analyzed_human_type?: string
  }
}

export interface ITpaSelfInsured {
  elected?: ITpaSelfInsuredQuestion
  declined?: ITpaSelfInsuredQuestion
  no_response?: ITpaSelfInsuredQuestion
}

export interface ITpaSelfInsuredQuestion {
  any_clients?: YES_OR_NO
  number_of_groups?: string
  covered_lives?: string
  claims_volume?: string
  aggregated_claim_value?: string
}
